import { Link } from 'react-router-dom';

import { isBaseDeviationType } from '@clm/shared-types';

import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { BASE_DEVIATION_OFFER_COPY, BASE_DEVIATION_READER_ROLES } from './base-deviation-shared';

/**
 * The deviation report, offered at intake — CLM-112 (FR-38 "the user is
 * offered", R-112-16). One line with a record link, on the upload success
 * panel, on each filed row of Add contract's *Several (PDF or ZIP)* mode
 * (CLM-57 — it files signed MSAs too), and on each bulk-ingest result row
 * whose type is already MSA. The first two read the **resolved** type from the
 * upload response (CLM-110, R-110-16: the uploader's type is only a hint, so a
 * blank hint the file name or a superseded agreement makes an MSA is still
 * offered) and the relationship from the request as submitted. The Admin's
 * type decision offers it too, once a type is confirmed as MSA.
 *
 * Never a prompt to run anything: at ingest there is no text (intake precedes
 * convert, rule 2), and segmentation and matching are operator-dispatched
 * stages, so there is no "ready" event to prompt on. The record is where the
 * report lives, and its section says what is still missing. Because it keys on
 * `contract_type`, a later CLM-110 reclassification to MSA makes the record's
 * section appear with no change here.
 *
 * Admin and Legal only (`clause:review_match`, Q-112-2) — a display hint; the
 * API decides. An amendment is never offered: it holds only changes, and an
 * MSA amendment carries its parent's type.
 */
export function BaseDeviationOffer({
  contractKey,
  contractType,
  isAmendment,
}: {
  readonly contractKey: string;
  readonly contractType: string;
  readonly isAmendment: boolean;
}) {
  const { identity } = useIdentity();
  if (!isBaseDeviationType(contractType) || isAmendment || !mayAttempt(identity, BASE_DEVIATION_READER_ROLES)) return null;
  return (
    <p className="mut" data-testid="base-deviation-offer">
      {BASE_DEVIATION_OFFER_COPY}{' '}
      <Link to={`/contracts/record/${encodeURIComponent(contractKey)}`}>Open {contractKey}</Link>
    </p>
  );
}
